import { useCallback, useMemo } from 'react'
import { useQueries } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { fetchFecAccountMappings } from '../query-api'
import type { FecAccountMapping } from '../types/fecAccountMapping'
import { dedupeFecAccountMappings } from '../utils/dedupeFecAccountMappings'
import { flattenFecAccountMappings } from '../utils/flattenFecAccountMappings'
import { uniqueIds } from '../utils/uniqueIds'

export interface UseFetchFecAccountMappingsResult {
  accountMappings: FecAccountMapping[]
  isError: boolean
  isLoading: boolean
  isRetrying: boolean
  retry: () => void
}

export const useFetchFecAccountMappings = (fecImportIds: string[]): UseFetchFecAccountMappingsResult => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id || ''
  const uniqueFecImportIds = useMemo(() => uniqueIds(fecImportIds), [fecImportIds])

  const queries = useQueries(
    uniqueFecImportIds.map((fecImportId) => ({
      enabled: !!organizationId && !!fecImportId,
      queryFn: async (): Promise<FecAccountMapping[]> => {
        try {
          const response = await fetchFecAccountMappings(organizationId, fecImportId)

          return flattenFecAccountMappings(response, fecImportId)
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)

          throw new Error(`FEC account mappings fetch failed for import ${fecImportId}: ${message}`)
        }
      },
      queryKey: [QueryKeys.FecAccountMappings, organizationId, fecImportId],
    })),
  )

  const dataUpdatedAtKey = queries.map((query) => query.dataUpdatedAt).join('|')

  const accountMappings = useMemo(
    () => dedupeFecAccountMappings(queries.flatMap((query) => query.data || [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by dataUpdatedAt snapshots
    [dataUpdatedAtKey],
  )

  const retry = useCallback(() => {
    for (const query of queries) {
      if (query.isError) {
        query.refetch()
      }
    }
  }, [queries])

  return {
    accountMappings,
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading || query.isIdle),
    isRetrying: queries.some((query) => query.isError && query.isFetching),
    retry,
  }
}
